import React, { useEffect, useState } from 'react';
import '../App.css';
import '../enquiry-ui.css';
import Icon from '../components/Icon';
import MasterCard from '../components/MasterCard';
import { fetchDashboard } from '../services/dashboardApi';
import {
  bulkUpdateStatus, deleteEnquiry, exportEnquiries, fetchEnquiries,
  fetchBin, fetchFilterOptions, fetchImportTemplate, importEnquiries,
  permanentlyDeleteFromBin, restoreFromBin, updateEnquiryStatus,
} from '../services/enquiriesApi';
import {
  createLocation, deleteLocation, fetchLocations, updateLocation,
} from '../services/locationsApi';
import { Country, State, City } from 'country-state-city';
import ListingPagination from '../components/ListingPagination';
import { createMailId, deleteMailId, fetchMailIds, saveDefaultMailIds, updateMailId } from '../services/mailSettingsApi';

const INDIA_STATES = State.getStatesOfCountry('IN');
const INDIA_STATE_NAMES = INDIA_STATES.map(s => s.name);
const builtInCityNames = stateName => {
  const state = INDIA_STATES.find(s => s.name.toLowerCase() === stateName.trim().toLowerCase());
  return state ? City.getCitiesOfState('IN', state.isoCode).map(c => c.name) : [];
};

const TYPE = 'export';
const MAIL_ID_BRANDS = ['Apsara', 'Nataraj', 'Custom', 'OEM Specification'];
const ENQUIRY_STATUSES = ['New', 'In Progress', 'Contacted', 'Closed'];
const STATUS_STYLE = {
  New: { color: '#c67f2f', background: '#fff1e6' },
  'In Progress': { color: '#3982c9', background: '#e9f4ff' },
  Contacted: { color: '#5654d7', background: '#eeeeff' },
  Closed: { color: '#29956c', background: '#e9f8f1' },
};
const PAGE_SIZE = 20;
const DEFAULT_PAGE_SIZE = 10;
const notifyCountChange = () => window.dispatchEvent(new Event('enquiry-counts-changed'));

const FIELD_LABELS = {
  agency_state: 'Agency State', agency_city: 'Agency City',
  first_name: 'First Name', middle_name: 'Middle Name', last_name: 'Last Name',
  dob: 'Date of Birth', mobile: 'Mobile Number', email: 'Email',
  educational_details: 'Educational Details',
  current_address: 'Current Address', current_state: 'Current State', current_city: 'Current City', current_pincode: 'Current Pincode',
  experience: 'Experience', designation: 'Designation', work_profile: 'Work Profile',
  company_name: 'Company Name', company_email: 'Company Email',
  company_address: 'Company Address', company_state: 'Company State', company_city: 'Company City', company_pincode: 'Company Pincode',
  firm_type: 'Firm Type', yearly_turnover: 'Yearly Turnover', year_of_establishment: 'Year of Establishment',
  partner_first_name: 'Partner First Name', partner_middle_name: 'Partner Middle Name', partner_last_name: 'Partner Last Name',
  partner_dob: 'Partner Date of Birth', partner_mobile: 'Partner Mobile Number', partner_email: 'Partner Email',
  partner_address: 'Partner Address', partner_state: 'Partner State', partner_city: 'Partner City',
  product_details: 'Product Details',
  storage_facility_availability: 'Storage Facility Availability', godown_size: 'Godown Size',
  transport_facility_availability: 'Transport Facility Availability', vehicle_model: 'Vehicle Model', no_of_vehicles: 'No. of Vehicles',
  investment_capacity: 'Investment Capacity', working_capital: 'Working Capital',
  current_business: 'Current Business', size_of_business: 'Size of Business', primary_contact_person: 'Primary Contact Person',
  business_details: 'Business Details', current_investment: 'Current Investment', infrastructure_details: 'Infrastructure Details',
  createdAt: 'Received On', updatedAt: 'Last Updated',
};


const contactName = e => e.contactPerson || '-';
const IST_TIME_ZONE = 'Asia/Kolkata';
const formatDate = iso => (iso ? new Date(iso).toLocaleString('en-IN', {
  timeZone: IST_TIME_ZONE,
  day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
}) : '-');
const todayInIst = () => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const value = type => parts.find(part => part.type === type)?.value;
  return `${value('year')}-${value('month')}-${value('day')}`;
};

function PageHeader({ eyebrow, name, description, action }) {
  return <div className="page-heading"><div><span>{eyebrow}</span><h1>{name}</h1><p>{description}</p></div>{action}</div>;
}

function StatusBadge({ status }) {
  const style = STATUS_STYLE[status] || {};
  return <span className="status" style={style}><i className="status-dot" style={{ background: style.color }} /> {status}</span>;
}

const selectStyle = { minHeight: 36, border: '1px solid #dfe1e9', borderRadius: 7, padding: '0 8px', background: '#fff', color: '#313444', fontSize: 12, fontWeight: 600 };
const fieldLabel = k => FIELD_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

function triggerDownload(result) {
  const mime = result.filename.endsWith('.csv')
    ? 'text/csv'
    : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const bytes = Uint8Array.from(atob(result.data), c => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = result.filename; a.click(); URL.revokeObjectURL(url);
}

/* ------------------------------- Overview ------------------------------- */

function TrendChart({ trend }) {
  const totals = trend.map(p => p.export);
  const max = Math.max(1, ...totals);
  const niceMax = Math.max(4, Math.ceil(max / 4) * 4);
  const n = trend.length;
  const x = i => (n > 1 ? (i / (n - 1)) * 700 : 0);
  const y = v => 210 - (v / niceMax) * 210;
  const line = totals.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L 700,210 L 0,210 Z`;
  const yLabels = [niceMax, Math.round((niceMax * 2) / 3), Math.round(niceMax / 3), 0];
  return (
    <section className="panel analytics-panel">
      <div className="panel-title"><div><h2>Enquiry Analytics</h2><p>Export enquiries received over the last {n} days</p></div></div>
      <div className="chart-area">
        <div className="y-labels">{yLabels.map((l, i) => <span key={i}>{l}</span>)}</div>
        <div className="chart-plot">
          <div className="grid-line one" /><div className="grid-line two" /><div className="grid-line three" />
          <svg viewBox="0 0 700 210" preserveAspectRatio="none">
            <defs><linearGradient id="fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#5b5bd6" stopOpacity=".22" /><stop offset="1" stopColor="#5b5bd6" stopOpacity="0" /></linearGradient></defs>
            <path className="area" d={area} />
            <path className="line" d={line} />
          </svg>
          <div className="months">{trend.filter((_, i) => i % 2 === 0).map(p => <span key={p.date}>{new Date(`${p.date}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>)}</div>
        </div>
      </div>
    </section>
  );
}

function Overview() {
  const [dashboard, setDashboard] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchDashboard().catch(() => null),
      fetchEnquiries(TYPE, { limit: 6, sortBy: 'createdAt', sortDir: 'desc' }).catch(() => ({ data: [] })),
    ]).then(([dash, list]) => {
      if (cancelled) return;
      setDashboard(dash);
      setRecent(list.data || []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <p className="api-message">Loading dashboard...</p>;
  const s = dashboard?.stats;
  const stats = [
    ['Total Enquiries', s?.total ?? 0, 'All-time export enquiries', 'purple', 'users'],
    ["Today's Enquiries", s?.today ?? 0, 'Received today', 'orange', 'clock'],
    ['Enquiries This Month', s?.thisMonth ?? 0, 'Received this month', 'blue', 'trend'],
    ['New Enquiries', s?.newEnquiries ?? 0, 'Awaiting first contact', 'orange', 'plus'],
    ['Closed Enquiries', s?.closed ?? 0, 'Completed enquiries', 'green', 'check'],
  ];
  return <>
    <PageHeader eyebrow="EXPORT INQUIRY OVERVIEW" name="Dashboard" description="Live counts for the Export Inquiry form." />
    <div className="stat-grid">{stats.map(([label, value, note, color, icon]) => (
      <article className="stat-card" key={label}><div className={`stat-icon ${color}`}><Icon name={icon} /></div><div className="stat-copy"><span>{label}</span><strong>{value}</strong><small>{note}</small></div></article>
    ))}</div>
  </>;
}

/* ---------------------------- Enquiry section --------------------------- */

const emptyFilters = { status: '', state: '', city: '', dateFrom: '', dateTo: '' };

function ImportModal({ title, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [rowErrors, setRowErrors] = useState([]);

  const downloadTemplate = async () => {
    setDownloading(true); setError('');
    try { triggerDownload(await fetchImportTemplate(TYPE)); }
    catch (err) { setError(err.message); }
    finally { setDownloading(false); }
  };

  const upload = async e => {
    e.preventDefault();
    if (!file) { setError('Please choose a file'); return; }
    setUploading(true); setError(''); setRowErrors([]);
    try {
      const res = await importEnquiries(TYPE, file);
      onDone(res.inserted || 0);
    } catch (err) { setError(err.message); setRowErrors(err.details || []); }
    finally { setUploading(false); }
  };

  return <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
    <form className="import-modal" onSubmit={upload}>
      <h2>Import {title}</h2>
      <p>Upload an .xlsx or .csv file. Download the template to see the required columns. Max 5 MB / 2000 rows.</p>
      <button type="button" className="secondary-action bulk-template-button" onClick={downloadTemplate} disabled={downloading}><Icon name="download" /> {downloading ? 'Preparing...' : 'Download template'}</button>
      <label className={`bulk-drop-zone${file ? ' selected' : ''}`} htmlFor="imp-export" style={{ marginTop: 18 }}>
        <Icon name="file" /><strong>{file ? file.name : 'Choose a file'}</strong>
        <span>{file ? `${(file.size / 1024).toFixed(1)} KB selected` : 'Click to browse (.xlsx, .csv)'}</span>
        <input id="imp-export" type="file" accept=".xlsx,.xls,.csv" onChange={e => { setFile(e.target.files?.[0] || null); setError(''); setRowErrors([]); }} />
      </label>
      {error && <div className="bulk-upload-error"><strong>{error}</strong>{rowErrors.length > 0 && <ul>{rowErrors.slice(0, 20).map((it, i) => <li key={i}>Row {it.row}: {it.message}</li>)}</ul>}{rowErrors.length > 20 && <small>And {rowErrors.length - 20} more.</small>}</div>}
      <div className="confirm-actions"><button type="button" className="secondary-action" onClick={onClose}>Cancel</button><button type="submit" className="primary-action" disabled={!file || uploading}><Icon name="upload" /> {uploading ? 'Importing...' : 'Import'}</button></div>
    </form>
  </div>;
}

function EnquirySection() {
  const title = 'Export Enquiries';
  const maxFilterDate = todayInIst();
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState({ by: 'createdAt', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [reload, setReload] = useState(0);
  const [resp, setResp] = useState({ data: [], pagination: { page: 1, total: 0, totalPages: 1, limit: PAGE_SIZE } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [options, setOptions] = useState({ states: [], cities: [], statuses: ENQUIRY_STATUSES });
  const [selected, setSelected] = useState(() => new Set());
  const [bulkStatus, setBulkStatus] = useState('New');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailStatus, setDetailStatus] = useState('New');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [actionError, setActionError] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const refresh = () => setReload(n => n + 1);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { fetchFilterOptions(TYPE).then(setOptions).catch(() => {}); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { page, limit: pageSize, sortBy: sort.by, sortDir: sort.dir, q: debounced, ...filters };
    fetchEnquiries(params)
      .then(res => { if (!cancelled) { setResp({ data: res.data || [], pagination: res.pagination || {} }); setError(''); setSelected(new Set()); } })
      .catch(err => { if (!cancelled) { setError(err.message); setResp({ data: [], pagination: { page: 1, total: 0, totalPages: 1 } }); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, sort, debounced, filters, reload]);

  const setFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };
  const clearAll = () => { setSearch(''); setDebounced(''); setFilters(emptyFilters); setSort({ by: 'createdAt', dir: 'desc' }); setPage(1); };
  const changeSort = field => { setSort(s => ({ by: field, dir: s.by === field && s.dir === 'desc' ? 'asc' : 'desc' })); setPage(1); };

  const { data, pagination } = resp;
  const totalPages = pagination.totalPages || 1;
  const hasFilters = search || filters.status || filters.state || filters.city || filters.dateFrom || filters.dateTo;

  const toggleRow = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allChecked = data.length > 0 && data.every(e => selected.has(e._id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(data.map(e => e._id)));

  const rowStatusChange = async (e, status) => {
    setActionError('');
    try { await updateEnquiryStatus(TYPE, e._id, status); refresh(); }
    catch (err) { setActionError(err.message); }
  };
  const applyBulk = async () => {
    setActionError(''); setBulkUpdating(true);
    try { await bulkUpdateStatus(TYPE, [...selected], bulkStatus); refresh(); }
    catch (err) { setActionError(err.message); }
    finally { setBulkUpdating(false); }
  };
  const confirmDelete = async () => {
    setActionError('');
    try { await deleteEnquiry(TYPE, pendingDelete._id); setPendingDelete(null); refresh(); notifyCountChange(); }
    catch (err) { setActionError(err.message); }
  };
  const runExport = async format => {
    setActionError('');
    const params = { q: debounced, ...filters, sortBy: sort.by, sortDir: sort.dir };
    try { triggerDownload(await exportEnquiries(TYPE, params, format)); }
    catch (err) { setActionError(err.message); }
  };
  const openDetails = e => { setDetails(e); setDetailStatus(e.status || 'New'); };
  const saveDetailStatus = async () => {
    try { const updated = await updateEnquiryStatus(TYPE, details._id, detailStatus); setDetails(updated); refresh(); }
    catch (err) { setActionError(err.message); }
  };

  const headers = [
    ['COMPANY', 'companyName'], ['CONTACT', 'contactPerson'], ['PHONE', 'phone'],
    ['COUNTRY', 'country'], ['PORT', 'portOfDestination'], ['STATUS', 'status'], ['RECEIVED', 'createdAt'],
  ];
  const pageWindow = () => {
    const pages = [];
    const from = Math.max(1, page - 2);
    const to = Math.min(totalPages, from + 4);
    for (let p = from; p <= to; p++) pages.push(p);
    return pages;
  };

  return <>
    <PageHeader eyebrow="EXPORT INQUIRY" name={title} description={`${pagination.total ?? 0} export enquiries in total.`}
      action={<span className="toolbar-actions">
        <button type="button" className="secondary-action" onClick={() => setShowImport(true)}><Icon name="upload" /> Import</button>
        <details className={`export-menu${!data.length ? ' disabled' : ''}`}>
          <summary className="secondary-action" aria-disabled={!data.length} onClick={event => { if (!data.length) event.preventDefault(); }}><Icon name="download" /> Export <span className="export-chevron" aria-hidden="true" /></summary>
          <div className="export-menu-options">
            <button type="button" disabled={!data.length} onClick={event => { event.currentTarget.closest('details').removeAttribute('open'); runExport('csv'); }}><span><strong>Export as CSV</strong></span></button>
            <button type="button" disabled={!data.length} onClick={event => { event.currentTarget.closest('details').removeAttribute('open'); runExport('xlsx'); }}><span><strong>Export as Excel</strong></span></button>
          </div>
        </details></span>} />

    <section className="panel jobs-panel">
      <div className="filter-bar">
        <input className="filter-search" placeholder="Search company, contact, email, phone..." value={search} onChange={e => setSearch(e.target.value)} />
        <select value={filters.status} onChange={e => setFilter('status', e.target.value)}><option value="">All statuses</option>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
        <select value={filters.state} onChange={e => setFilter('state', e.target.value)}><option value="">All countries</option>{options.states.map(s => <option key={s}>{s}</option>)}</select>
        <select value={filters.city} onChange={e => setFilter('city', e.target.value)}><option value="">All ports</option>{options.cities.map(c => <option key={c}>{c}</option>)}</select>
        <label>From<input type="date" max={maxFilterDate} value={filters.dateFrom} onChange={e => setFilter('dateFrom', e.target.value)} /></label>
        <label>To<input type="date" max={maxFilterDate} value={filters.dateTo} onChange={e => setFilter('dateTo', e.target.value)} /></label>
        <button type="button" className="reset-filters" onClick={clearAll} disabled={!hasFilters} aria-label="Reset all enquiry filters">Reset filters</button>
      </div>

      {selected.size > 0 && <div className="bulk-bar status-bulk-bar">
        <div className="bulk-selection-count"><span>{selected.size}</span><div><strong>{selected.size === 1 ? 'Enquiry selected' : 'Enquiries selected'}</strong><small>Update all selected records together</small></div></div>
        <div className="bulk-actions">
          <label className="bulk-status-field"><span>Change status</span><select value={bulkStatus} disabled={bulkUpdating} onChange={e => setBulkStatus(e.target.value)}>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select></label>
          <button type="button" className="bulk-apply-button" disabled={bulkUpdating} onClick={applyBulk}><Icon name="check" />{bulkUpdating ? 'Updating…' : 'Apply status'}</button>
          <button type="button" className="clear-selection" disabled={bulkUpdating} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      </div>}

      {actionError && <p className="api-message error">{actionError}</p>}
      {error && <p className="api-message error">{error}</p>}

      {loading ? <p className="api-message">Loading enquiries...</p> : (
        <div className="table-wrap"><table>
          <thead><tr>
            <th className="checkbox-cell"><input type="checkbox" className="row-check" checked={allChecked} onChange={toggleAll} aria-label="Select all" /></th>
            {headers.map(([label, field]) => (
              <th key={field} className="sortable" onClick={() => changeSort(field)}>{label}{sort.by === field && <span className="sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}</th>
            ))}
            <th>ACTIONS</th>
          </tr></thead>
          <tbody>{data.length === 0
            ? <tr><td colSpan={headers.length + 2} style={{ color: '#9a9daa' }}>No matching enquiries.</td></tr>
            : data.map(e => <tr key={e._id}>
              <td className="checkbox-cell"><input type="checkbox" className="row-check" checked={selected.has(e._id)} onChange={() => toggleRow(e._id)} aria-label={`Select ${contactName(e)}`} /></td>
              <td><strong>{e.companyName || '-'}</strong></td>
              <td>{contactName(e)}</td>
              <td>{e.phone || '-'}</td>
              <td>{e.country || '-'}</td>
              <td>{e.portOfDestination || '-'}</td>
              <td><select value={e.status || 'New'} onChange={ev => rowStatusChange(e, ev.target.value)} style={{ ...selectStyle, ...STATUS_STYLE[e.status || 'New'] }}>{ENQUIRY_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}</select></td>
              <td>{formatDate(e.createdAt)}</td>
              <td><span className="job-row-actions">
                <button type="button" className="listing-action-button edit" onClick={() => openDetails(e)} aria-label="View details" title="View"><Icon name="file" /></button>
                <button type="button" className="listing-action-button delete" onClick={() => { setActionError(''); setPendingDelete(e); }} aria-label="Delete" title="Delete"><Icon name="delete" /></button>
              </span></td>
            </tr>)}</tbody></table></div>
      )}

            {!loading && <ListingPagination page={page} pageSize={pageSize} total={pagination.total || 0} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />}

    </section>

    {details && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setDetails(null); }}>
      <div className="entry-modal" role="dialog" aria-modal="true" style={{ width: 'min(640px,100%)', maxHeight: '82vh', overflow: 'auto' }}>
        <h2>{details.companyName || contactName(details)}</h2>
        <p>Export enquiry details · received {formatDate(details.createdAt)}</p>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
          <span className="field-label">Status</span>
          <select value={detailStatus} onChange={e => setDetailStatus(e.target.value)} style={selectStyle}>{ENQUIRY_STATUSES.map(s => <option key={s}>{s}</option>)}</select>
          <button type="button" className="primary-action" onClick={saveDetailStatus} disabled={detailStatus === details.status}>Save</button>
        </div> */}
        <div className="master-table-wrap"><table className="master-table"><tbody>
          {Object.entries(details).filter(([k]) => !['_id', '__v', 'status', 'updatedAt'].includes(k)).map(([k, v]) => (
            <tr key={k}><td style={{ color: '#8d90a1', width: 210, textTransform: 'capitalize' }}>{k.replace(/([A-Z])/g, ' $1')}</td><td><strong>{['createdAt', 'updatedAt', 'deletedAt'].includes(k) ? formatDate(v) : String(v || '-')}</strong></td></tr>
          ))}
        </tbody></table></div>
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setDetails(null)}>Close</button></div>
      </div>
    </div>}

    {pendingDelete && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setPendingDelete(null); }}>
      <div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>Delete Enquiry?</h2>
        <p>Move the enquiry from <strong>"{pendingDelete.companyName || contactName(pendingDelete)}"</strong> to Export Bin? You can restore it later.</p>
        {actionError && <div className="entry-error">{actionError}</div>}
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setPendingDelete(null)}>Cancel</button><button type="button" className="danger-action" onClick={confirmDelete}>Move to Export Bin</button></div>
      </div>
    </div>}

    {showImport && <ImportModal title={title} onClose={() => setShowImport(false)} onDone={n => { setShowImport(false); setActionError(''); clearAll(); refresh(); notifyCountChange(); window.alert(`${n} enquiries imported.`); }} />}
  </>;
}

/* --------------------------- Location master ---------------------------- */

// const LOCATION_LEVELS = [
//   { key: 'states', name: 'State', plural: 'States', icon: 'S', color: 'blue', description: 'Manage states available on the enquiry forms', parents: [] },
//   { key: 'cities', name: 'City', plural: 'Cities', icon: 'C', color: 'orange', description: 'Manage cities, grouped by state', parents: ['state'] },
// ];

// function LocationMaster({ locations, setLocations }) {
//   const [selected, setSelected] = useState(null);
//   const [entryModal, setEntryModal] = useState(null);
//   const [form, setForm] = useState({ name: '', state: '', isActive: true });
//   const [entryError, setEntryError] = useState('');
//   const [busy, setBusy] = useState(false);
//   const [pendingDelete, setPendingDelete] = useState(null);

//   const config = LOCATION_LEVELS.find(l => l.key === selected);
//   const items = config ? locations[config.key] : [];
//   const stateNameOptions = Array.from(new Set([...INDIA_STATE_NAMES, ...locations.states.map(s => s.name)])).sort((a, b) => a.localeCompare(b));

//   const openModal = (mode, item = null) => {
//     setEntryModal({ mode, item });
//     const parentValues = {};
//     config.parents.forEach(p => { parentValues[p] = item?.[p] || ''; });
//     setForm({ name: item?.name || '', ...parentValues, isActive: item?.isActive ?? true });
//     setEntryError('');
//   };
//   const closeModal = () => { setEntryModal(null); setEntryError(''); };

//   const save = async e => {
//     e.preventDefault();
//     const name = form.name.trim();
//     if (!name) { setEntryError(`${config.name} name is required`); return; }
//     for (const parent of config.parents) if (!form[parent].trim()) { setEntryError(`${parent} is required`); return; }
//     if (config.key === 'states' && INDIA_STATE_NAMES.some(s => s.toLowerCase() === name.toLowerCase())) {
//       setEntryError(`"${name}" is already available by default — no need to add it`);
//       return;
//     }
//     if (config.key === 'cities' && builtInCityNames(form.state).some(c => c.toLowerCase() === name.toLowerCase())) {
//       setEntryError(`"${name}" is already available by default under ${form.state} — no need to add it`);
//       return;
//     }
//     setBusy(true); setEntryError('');
//     try {
//       const payload = { name: form.name.trim(), isActive: form.isActive };
//       config.parents.forEach(p => { payload[p] = form[p].trim(); });
//       const saved = entryModal.mode === 'edit'
//         ? await updateLocation(config.key, entryModal.item._id, payload)
//         : await createLocation(config.key, payload);
//       setLocations(current => ({
//         ...current,
//         [config.key]: entryModal.mode === 'edit'
//           ? current[config.key].map(i => (i._id === saved._id ? saved : i))
//           : [...current[config.key], saved].sort((a, b) => a.name.localeCompare(b.name)),
//       }));
//       closeModal();
//     } catch (err) { setEntryError(err.message); }
//     finally { setBusy(false); }
//   };

//   const remove = async () => {
//     setBusy(true); setEntryError('');
//     try {
//       await deleteLocation(config.key, pendingDelete._id);
//       setLocations(current => ({ ...current, [config.key]: current[config.key].filter(i => i._id !== pendingDelete._id) }));
//       setPendingDelete(null);
//     } catch (err) { setEntryError(err.message); }
//     finally { setBusy(false); }
//   };

//   if (!config) return <>
//     <PageHeader eyebrow="MASTERS" name="Location Master" description="Add any state or city missing from the enquiry-form dropdowns." />
//     <div className="master-category-grid">
//       {LOCATION_LEVELS.map(l => <MasterCard key={l.key} title={l.plural} description={l.description} count={locations[l.key].length} icon={l.icon} color={l.color} onClick={() => setSelected(l.key)} />)}
//     </div>
//   </>;

//   return <>
//     <div className="master-detail-header"><button onClick={() => setSelected(null)}><Icon name="arrowLeft" /> Back to Location Master</button></div>
//     <PageHeader eyebrow="MASTER MANAGEMENT" name={config.plural} description={`Add, view, and remove ${config.plural.toLowerCase()}.`} />
//     <section className="panel master-detail-panel">
//       <div className="master-detail-title"><span className={`master-category-icon ${config.color}`}>{config.icon}</span><div><h2>{config.plural}</h2><p>{items.length} entries available</p></div><button className="primary-action master-add-button" onClick={() => openModal('add')}><Icon name="plus" /> Add {config.name}</button></div>
//       <div className="master-table-wrap"><table className="master-table"><thead><tr><th>#</th>{config.parents.map(p => <th key={p}>{p.toUpperCase()}</th>)}<th>{config.name.toUpperCase()} NAME</th><th>STATUS</th><th>CREATED</th><th>ACTION</th></tr></thead>
//         <tbody>{items.length === 0
//           ? <tr><td colSpan={5 + config.parents.length} style={{ color: '#9a9daa' }}>No {config.plural.toLowerCase()} added yet.</td></tr>
//           : items.map((item, index) => <tr key={item._id}>
//             <td>{String(index + 1).padStart(2, '0')}</td>
//             {config.parents.map(p => <td key={p}>{item[p]}</td>)}
//             <td><span className="master-table-name"><i>{item.name[0]}</i><strong>{item.name}</strong></span></td>
//             <td><span className={`master-active-status${item.isActive ? '' : ' inactive'}`}><i className="status-dot" /> {item.isActive ? 'Active' : 'Inactive'}</span></td>
//             <td>{item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-'}</td>
//             <td><span className="master-row-actions">
//               <button className="listing-action-button edit" type="button" onClick={() => openModal('edit', item)} aria-label={`Edit ${item.name}`} title="Edit"><Icon name="edit" /></button>
//               <button className="listing-action-button delete" type="button" onClick={() => setPendingDelete(item)} aria-label={`Delete ${item.name}`} title="Delete"><Icon name="delete" /></button>
//             </span></td>
//           </tr>)}</tbody></table></div>
//     </section>

//     {entryModal && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) closeModal(); }}>
//       <form className="entry-modal" role="dialog" aria-modal="true" onSubmit={save}>
//         <div className={`master-category-icon ${config.color}`}>{config.icon}</div>
//         <h2>{entryModal.mode === 'edit' ? 'Edit' : 'Add'} {config.name}</h2>
//         <p>{entryModal.mode === 'edit' ? `Update the selected ${config.name.toLowerCase()}.` : `Create a new ${config.name.toLowerCase()} for the enquiry forms.`}</p>
       
//         {config.parents.includes('state') && <label>State
//           <select value={form.state} onChange={e => { setForm({ ...form, state: e.target.value }); setEntryError(''); }}>
//             <option value="">Select a state</option>
//             {stateNameOptions.map(s => <option key={s} value={s}>{s}</option>)}
//           </select>
//         </label>}
//         <label>{config.name} name<input autoFocus value={form.name} onChange={e => { setForm({ ...form, name: e.target.value }); setEntryError(''); }} placeholder={`Enter ${config.name.toLowerCase()} name`} /></label>
//         <label>Status<select value={form.isActive ? 'active' : 'inactive'} onChange={e => setForm({ ...form, isActive: e.target.value === 'active' })}><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
//         {entryError && <div className="entry-error">{entryError}</div>}
//         <div className="confirm-actions"><button type="button" className="secondary-action" onClick={closeModal}>Cancel</button><button disabled={busy} className="primary-action">{busy ? 'Saving...' : entryModal.mode === 'edit' ? 'Save changes' : `Add ${config.name}`}</button></div>
//       </form>
//     </div>}

//     {pendingDelete && <div className="modal-backdrop" onMouseDown={e => { if (e.target === e.currentTarget) setPendingDelete(null); }}>
//       <div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon">!</div><h2>Delete {config.name}?</h2>
//         <p>Delete <strong>"{pendingDelete.name}"</strong>? It will no longer appear in the enquiry-form dropdowns.</p>
//         {entryError && <div className="entry-error">{entryError}</div>}
//         <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setPendingDelete(null)}>Cancel</button><button disabled={busy} type="button" className="danger-action" onClick={remove}>{busy ? 'Deleting...' : 'Delete entry'}</button></div>
//       </div>
//     </div>}
//   </>;
// }

// function LocationMasterSection() {
//   const [locations, setLocations] = useState(null);
//   useEffect(() => { fetchLocations().then(setLocations).catch(() => setLocations({ states: [], cities: [] })); }, []);
//   if (!locations) return <p className="api-message">Loading location master...</p>;
//   return <LocationMaster locations={locations} setLocations={setLocations} />;
// }

function BinListing({ items, loading, error, onRestore, onPermanentDelete }) {
  const itemLabel = count => `${count} export ${count === 1 ? 'enquiry' : 'enquiries'}`;
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState(null);
  const [details, setDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const pagedItems = items.slice((page - 1) * pageSize, page * pageSize);
  const allChecked = pagedItems.length > 0 && pagedItems.every(item => selected.has(item._id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(pagedItems.map(item => item._id)));
  const toggleRow = id => setSelected(current => { const next = new Set(current); next.has(id) ? next.delete(id) : next.add(id); return next; });
  const requestAction = (action, ids) => { setActionError(''); setPending({ action, ids }); };
  useEffect(() => { setPage(current => Math.min(current, Math.max(1, Math.ceil(items.length / pageSize)))); }, [items.length, pageSize]);
  const runAction = async () => {
    setActionLoading(true); setActionError('');
    try {
      if (pending.action === 'restore') await onRestore(pending.ids);
      else await onPermanentDelete(pending.ids);
      setSelected(current => { const next = new Set(current); pending.ids.forEach(id => next.delete(id)); return next; });
      setPending(null);
    } catch (actionErr) { setActionError(actionErr.message); }
    finally { setActionLoading(false); }
  };
  return <>
    <PageHeader eyebrow="ARCHIVE" name="Export Bin" description={`${itemLabel(items.length)} retained in the database.`} />
    {error && <p className="api-message error">{error}</p>}
    {loading ? <p className="api-message">Loading deleted export enquiries...</p> : <div className="bin-grid"><section className="panel jobs-panel bin-panel">
      <div className="panel-title"><div><h2>Export Enquiries</h2><p>{items.length} deleted export {items.length === 1 ? 'enquiry' : 'enquiries'}</p></div></div>
      {selected.size > 0 && <div className="bulk-bar bin-bulk-bar">
        <div className="bulk-selection-count"><span>{selected.size}</span><div><strong>{selected.size === 1 ? 'Enquiry selected' : 'Enquiries selected'}</strong><small>Choose what to do with the selected records</small></div></div>
        <div className="bulk-actions">
          <button type="button" className="bin-restore-button" disabled={actionLoading} onClick={() => requestAction('restore', [...selected])}><span className="bin-action-icon">&#8634;</span><span>Restore selected</span></button>
          <button type="button" className="bin-delete-button" disabled={actionLoading} onClick={() => requestAction('delete', [...selected])}><Icon name="delete" /><span>Delete permanently</span></button>
          <button type="button" className="clear-selection" disabled={actionLoading} onClick={() => setSelected(new Set())}>Clear selection</button>
        </div>
      </div>}
      <div className="table-wrap"><table><thead><tr><th className="checkbox-cell"><input type="checkbox" className="row-check" checked={allChecked} onChange={toggleAll} aria-label="Select all deleted export enquiries" /></th><th>COMPANY</th><th>CONTACT</th><th>EMAIL</th><th>STATUS</th><th>MOVED TO BIN</th><th>ACTIONS</th></tr></thead><tbody>
        {pagedItems.map(item => <tr key={item._id}><td className="checkbox-cell"><input type="checkbox" className="row-check" checked={selected.has(item._id)} onChange={() => toggleRow(item._id)} aria-label={`Select ${item.companyName || 'enquiry'}`} /></td><td><strong>{item.companyName || '-'}</strong></td><td>{item.contactPerson || '-'}</td><td>{item.email || '-'}</td><td><StatusBadge status={item.status || 'New'} /></td><td>{formatDate(item.deletedAt)}</td><td><span className="job-row-actions"><button type="button" className="listing-action-button edit" onClick={() => setDetails(item)} title="View details" aria-label="View export enquiry details"><Icon name="file" /></button><button type="button" className="listing-action-button restore" disabled={actionLoading} onClick={() => requestAction('restore', [item._id])} title="Restore" aria-label="Restore export enquiry">&#8634;</button><button type="button" className="listing-action-button delete" disabled={actionLoading} onClick={() => requestAction('delete', [item._id])} title="Delete permanently" aria-label="Delete export enquiry permanently"><Icon name="delete" /></button></span></td></tr>)}
        {!items.length && <tr><td colSpan="7" className="empty-table-cell">No deleted export enquiries.</td></tr>}
      </tbody></table></div>
      <ListingPagination page={page} pageSize={pageSize} total={items.length} onPageChange={setPage} onPageSizeChange={size => { setPageSize(size); setPage(1); }} />
    </section></div>}
    {details && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget) setDetails(null); }}>
      <div className="entry-modal" role="dialog" aria-modal="true" style={{ width: 'min(720px,100%)', maxHeight: '82vh', overflow: 'auto' }}>
        <div className="master-category-icon blue"><Icon name="file" /></div>
        <h2>{details.companyName || 'Enquiry Details'}</h2>
        <p>Complete archived export enquiry record.</p>
        <div className="master-table-wrap"><table className="master-table"><tbody>
          {Object.entries(details).filter(([key]) => !['__v', 'title', 'department', 'city', 'fullName', 'jobTitle'].includes(key)).map(([key, value]) => (
            <tr key={key}><td style={{ color: '#8d90a1', width: 220 }}>{fieldLabel(key)}</td><td><strong>{value === null || value === undefined || value === '' ? '-' : ['createdAt', 'updatedAt', 'deletedAt'].includes(key) ? formatDate(value) : typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}</strong></td></tr>
          ))}
        </tbody></table></div>
        <div className="confirm-actions"><button type="button" className="secondary-action" onClick={() => setDetails(null)}>Close</button></div>
      </div>
    </div>}
    {pending && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !actionLoading) setPending(null); }}><div className={`confirm-modal ${pending.action === 'restore' ? 'restore-confirm' : 'delete-confirm'}`} role="dialog" aria-modal="true"><div className="confirm-icon">{pending.action === 'restore' ? <span>&#8634;</span> : <Icon name="delete" />}</div><h2>{pending.action === 'restore' ? 'Restore' : 'Permanently Delete'} {pending.ids.length > 1 ? 'Selected Enquiries' : 'Enquiry'}?</h2><p>{pending.action === 'restore' ? `${itemLabel(pending.ids.length)} will be returned to the active listing.` : `${itemLabel(pending.ids.length)} will be permanently deleted. This action cannot be undone.`}</p>{actionError && <div className="entry-error">{actionError}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={actionLoading} onClick={() => setPending(null)}>Cancel</button><button type="button" className={pending.action === 'restore' ? 'restore-action' : 'danger-action'} disabled={actionLoading} onClick={runAction}>{actionLoading ? 'Processing...' : pending.action === 'restore' ? 'Restore enquiry' : 'Delete permanently'}</button></div></div></div>}
  </>;
}

function BinSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchBin(TYPE);
      setItems(data || []);
      setError('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (action, ids) => {
    if (action === 'restore') {
      await restoreFromBin(TYPE, ids);
    }
    else await permanentlyDeleteFromBin(TYPE, ids);
    await load();
    notifyCountChange();
  };

  return <BinListing items={items} loading={loading} error={error} onRestore={ids => run('restore', ids)} onPermanentDelete={ids => run('delete', ids)} />;
}

function MailIdsSection() {
  const [entries, setEntries] = useState([]);
  const [defaultMailIds, setDefaultMailIds] = useState('');
  const [defaultSaving, setDefaultSaving] = useState(false);
  const [defaultMessage, setDefaultMessage] = useState('');
  const [defaultError, setDefaultError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState({ productInterests: [], countries: [], mailIds: '' });
  const [error, setError] = useState('');
  const countries = Country.getAllCountries();
  const selectedValues = event => [...event.target.selectedOptions].map(option => option.value);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchMailIds();
      setEntries(data.entries || []);
      setDefaultMailIds((data.defaultMailIds?.length ? data.defaultMailIds : data.legacyMailIds || []).join(', '));
      setError('');
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async event => {
    event.preventDefault();
    setSaving(true); setError('');
    try {
      if (editingEntry) await updateMailId(editingEntry._id, form);
      else await createMailId(form);
      setForm({ productInterests: [], countries: [], mailIds: '' });
      setEditingEntry(null);
      setShowForm(false);
      await load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const saveDefaults = async event => {
    event.preventDefault();
    setDefaultSaving(true); setDefaultError(''); setDefaultMessage('');
    try {
      const result = await saveDefaultMailIds(defaultMailIds);
      setDefaultMailIds((result.data.defaultMailIds || result.data.mailIds || []).join(', '));
      setDefaultMessage(result.message || 'Default mail IDs saved successfully');
    } catch (err) { setDefaultError(err.message); }
    finally { setDefaultSaving(false); }
  };

  const remove = async () => {
    setSaving(true); setError('');
    try { await deleteMailId(pendingDelete._id); setPendingDelete(null); await load(); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const openAdd = () => {
    setError('');
    setEditingEntry(null);
    setForm({ productInterests: [], countries: [], mailIds: '' });
    setShowForm(true);
  };

  const openEdit = entry => {
    setError('');
    setEditingEntry(entry);
    setForm({
      productInterests: [...entry.productInterests],
      countries: [...entry.countries],
      mailIds: entry.mailIds.join(', '),
    });
    setShowForm(true);
  };

  return <>
    <PageHeader eyebrow="NOTIFICATION SETTINGS" name="Mail IDs" description="Route export inquiry notifications by product interest and country." action={<button type="button" className="primary-action" onClick={openAdd}><Icon name="plus" /> Add Mail ID</button>} />
    {error && !showForm && !pendingDelete && <p className="api-message error">{error}</p>}
    <section className="panel default-mail-panel">
      <form onSubmit={saveDefaults}><div className="default-mail-copy"><h2>Default Mail IDs</h2><p>These optional recipients receive every enquiry, regardless of brand or country.</p></div><div className="default-mail-control"><textarea rows="2" value={defaultMailIds} onChange={event => setDefaultMailIds(event.target.value)} placeholder="exports@example.com, sales@example.com" /><small>Separate multiple email addresses with commas, or leave empty.</small></div><button type="submit" className="primary-action" disabled={defaultSaving}>{defaultSaving ? 'Saving...' : 'Save'}</button></form>
      {defaultError && <div className="form-error">{defaultError}</div>}{defaultMessage && <div className="api-message mail-settings-success">{defaultMessage}</div>}
    </section>
    <section className="panel jobs-panel mail-id-listing">
      <div className="panel-title"><div><h2>Mail ID Listing</h2><p>{entries.length} configured {entries.length === 1 ? 'recipient' : 'recipients'}</p></div></div>
      {loading ? <p className="api-message">Loading mail IDs...</p> : <div className="table-wrap"><table><thead><tr><th>#</th><th>BRAND / PRODUCT INTEREST</th><th>COUNTRY</th><th>MAIL ID</th><th>ACTION</th></tr></thead><tbody>
        {entries.map((entry, index) => <tr key={entry._id}><td>{String(index + 1).padStart(2, '0')}</td><td><strong>{entry.productInterests.join(', ')}</strong></td><td>{entry.countries.join(', ')}</td><td>{entry.mailIds.join(', ')}</td><td><span className="job-row-actions"><button type="button" className="listing-action-button edit" onClick={() => openEdit(entry)} title="Edit" aria-label={`Edit ${entry.mailIds.join(', ')}`}><Icon name="edit" /></button><button type="button" className="listing-action-button delete" onClick={() => { setError(''); setPendingDelete(entry); }} title="Delete" aria-label={`Delete ${entry.mailIds.join(', ')}`}><Icon name="delete" /></button></span></td></tr>)}
        {!entries.length && <tr><td colSpan="5" className="empty-table-cell">No mail IDs added yet. Use "Add Mail ID" to create the first one.</td></tr>}
      </tbody></table></div>}
    </section>
    {showForm && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !saving) setShowForm(false); }}><form className="entry-modal mail-id-form" onSubmit={submit} role="dialog" aria-modal="true">
      <div className="master-category-icon"><Icon name={editingEntry ? 'edit' : 'plus'} /></div><h2>{editingEntry ? 'Edit Mail IDs' : 'Add Mail IDs'}</h2><p>Select all applicable product interests and countries, then enter one or more recipient addresses.</p>
      <label>Brands<select multiple required size="4" value={form.productInterests} onChange={event => setForm({ ...form, productInterests: selectedValues(event) })}>{MAIL_ID_BRANDS.map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <small className="field-help">Hold Ctrl (Windows) or Command (Mac) to select multiple options.</small>
      <label>Countries<select multiple required size="6" value={form.countries} onChange={event => setForm({ ...form, countries: selectedValues(event) })}>{countries.map(country => <option key={country.isoCode} value={country.name}>{country.name}</option>)}</select></label>
      <small className="field-help">Hold Ctrl (Windows) or Command (Mac) to select multiple options.</small>
      <label>Mail IDs<textarea required value={form.mailIds} onChange={event => setForm({ ...form, mailIds: event.target.value })} placeholder="exports@example.com, sales@example.com" rows="3" /></label>
      <small className="field-help">Separate multiple email addresses with commas.</small>
      {error && <div className="entry-error">{error}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={saving} onClick={() => setShowForm(false)}>Cancel</button><button className="primary-action" disabled={saving}>{saving ? 'Saving...' : editingEntry ? 'Save Changes' : 'Add Mail IDs'}</button></div>
    </form></div>}
    {pendingDelete && <div className="modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !saving) setPendingDelete(null); }}><div className="confirm-modal" role="dialog" aria-modal="true"><div className="confirm-icon"><Icon name="delete" /></div><h2>Delete Mail IDs?</h2><p><strong>{pendingDelete.mailIds.join(', ')}</strong> will stop receiving the selected product-interest and country inquiries.</p>{error && <div className="entry-error">{error}</div>}<div className="confirm-actions"><button type="button" className="secondary-action" disabled={saving} onClick={() => setPendingDelete(null)}>Cancel</button><button type="button" className="danger-action" disabled={saving} onClick={remove}>{saving ? 'Deleting...' : 'Delete'}</button></div></div></div>}
  </>;
}
/* ------------------------------- Root page ------------------------------ */

function ChannelPartnerAdmin({ activeSection = 'overview' }) {
  if (activeSection === 'overview') return <Overview />;
  if (activeSection === 'export') return <EnquirySection />;
  if (activeSection === 'bin') return <BinSection />;
  if (activeSection === 'mail-ids') return <MailIdsSection />;

  //  if (activeSection === 'location-master') return <LocationMasterSection />;
 
  return null;
}

export default ChannelPartnerAdmin;
